/**
 * Vibed Pages Router version of FortuneReleaseModal.tsx
 *
 * Uses inline styles to simulate Tailwind styles.
 */
'use client';

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
// Use a local overlay/content to guarantee centering for pages-router modal
import {
  deleteFortuneReleaseCookie,
  getFortuneReleaseCookie,
  setFortuneReleaseCookie,
} from '@/app_lib/utils/fortuneReleaseCookie';

interface FortuneReleaseModalProps {
  trigger?: React.ReactNode;
}

export interface FortuneReleaseModalRef {
  openModal: () => void;
}

export const FortuneReleaseModal = forwardRef<FortuneReleaseModalRef, FortuneReleaseModalProps>(({ trigger }, ref) => {
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('beta');
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => {
      setIsOpen(true);
    },
  }));

  useEffect(() => {
    const value = getFortuneReleaseCookie();
    setCurrentValue(value);
  }, []);

  useEffect(() => {
    // Check authorization when modal opens
    if (isOpen && isAuthorized === null) {
      checkAuthorization();
    }
  }, [isOpen]);

  const checkAuthorization = async () => {
    setIsCheckingAuth(true);
    try {
      const response = await fetch('/api/fortune-release/authorize/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      setIsAuthorized(data.authorized === true);
    } catch {
      setIsAuthorized(false);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleSetCookie = () => {
    if (inputValue.trim()) {
      setFortuneReleaseCookie(inputValue.trim());
      setCurrentValue(inputValue.trim());
      window.location.reload();
    }
  };

  const handleDeleteCookie = () => {
    deleteFortuneReleaseCookie();
    setCurrentValue(null);
    window.location.reload();
  };

  // Modal body content with authorization states
  const getModalBodyContent = () => {
    if (isCheckingAuth) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>Checking authorization...</div>
        </div>
      );
    }

    if (isAuthorized === false) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626' }}>NOT AUTHORIZED</div>
          <p style={{ fontSize: '0.875rem', color: '#4b5563', marginTop: '0.5rem' }}>
            You do not have permission to access this feature.
          </p>
        </div>
      );
    }

    if (isAuthorized === true) {
      return (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
              Current Fortune Cookie Value:
            </div>
            <div
              style={{
                fontSize: '0.875rem',
                color: '#4b5563',
                backgroundColor: '#f9fafb',
                padding: '0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {currentValue ? (
                <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace' }}>
                  {currentValue}
                </code>
              ) : (
                <span style={{ fontStyle: 'italic', color: '#6b7280' }}>Not set (using production backend)</span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label
              style={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                display: 'block',
                marginBottom: '0.5rem',
              }}
              htmlFor='cookie-value'
            >
              Set Fortune Cookie Value:
            </label>
            <input
              id='cookie-value'
              onChange={(e) => setInputValue(e.target.value)}
              placeholder='Enter backend route value'
              type='text'
              value={inputValue}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                outline: 'none',
              }}
            />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
              This will route traffic to the specified backend service
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '1rem' }}>
            <button
              disabled={!inputValue.trim()}
              onClick={handleSetCookie}
              type='button'
              style={{
                flex: 1,
                backgroundColor: inputValue.trim() ? '#000000' : '#d1d5db',
                color: '#ffffff',
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: 'none',
                fontWeight: 500,
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              Set Cookie & Refresh
            </button>
            <button
              onClick={handleDeleteCookie}
              type='button'
              style={{
                flex: 1,
                backgroundColor: '#dc2626',
                color: '#ffffff',
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: 'none',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Delete Cookie & Refresh
            </button>
          </div>

          <p style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center', marginTop: '1rem' }}>
            Page will refresh automatically after setting or deleting the cookie
          </p>
        </>
      );
    }

    return null;
  };

  // Modal content styled with inline styles to avoid dependency on Tailwind in pages-router
  const modalContent = (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '0.5rem',
        boxShadow: '0 10px 15px rgba(0,0,0,0.08)',
        border: '1px solid #e5e7eb',
        width: '400px',
        maxWidth: '90vw',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>
          Fortune Cookie Release Manager
        </h2>
      </div>

      <div style={{ padding: '1.5rem' }}>{getModalBodyContent()}</div>
    </div>
  );

  return (
    <>
      {trigger}
      {isOpen && (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='fortune-release-title'
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            aria-hidden='true'
            onClick={() => setIsOpen(false)}
            style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
          />

          <div style={{ maxWidth: '90vw', width: '400px', position: 'relative', zIndex: 1 }}>
            <button
              type='button'
              onClick={() => setIsOpen(false)}
              aria-label='Close'
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#6b7280',
              }}
            >
              <span aria-hidden='true'>&times;</span>
            </button>
            <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>{modalContent}</div>
          </div>
        </div>
      )}
    </>
  );
});
